import { useCallback, useState } from 'react';
import { useTimelineStore } from '../store/timelineStore';
import { useHistoryStore, createHistoryAction, HISTORY_ACTIONS } from '../store/historyStore';

export const useClipEditor = () => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragType: null, // 'move' | 'trim-start' | 'trim-end'
    dragClipId: null,
    dragTrackId: null,
    startPosition: null
  });

  const {
    tracks,
    moveClipToTrack,
    reorderClipsInTrack,
    updateClip,
    deleteClip,
    trimClip
  } = useTimelineStore();

  const { addHistory } = useHistoryStore();

  // 开始拖拽片段
  const startDragClip = useCallback((clipId, trackId, dragType = 'move', startPosition) => {
    setDragState({
      isDragging: true,
      dragType,
      dragClipId: clipId,
      dragTrackId: trackId,
      startPosition
    });
  }, []);

  // 结束拖拽
  const endDragClip = useCallback(() => {
    setDragState({
      isDragging: false,
      dragType: null,
      dragClipId: null,
      dragTrackId: null,
      startPosition: null
    });
  }, []);

  // 移动片段到新轨道
  const moveClip = useCallback((clipId, fromTrackId, toTrackId, position = -1) => {
    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.MOVE_CLIP,
      '移动片段',
      {
        clipId,
        fromTrackId,
        toTrackId,
        fromPosition: position,
        toPosition: -1
      }
    ));

    moveClipToTrack(clipId, fromTrackId, toTrackId, position);
  }, [moveClipToTrack, addHistory]);

  // 重排片段顺序
  const reorderClips = useCallback((trackId, fromIndex, toIndex) => {
    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.REORDER_CLIPS,
      '重排片段',
      {
        trackId,
        fromIndex,
        toIndex
      }
    ));

    reorderClipsInTrack(trackId, fromIndex, toIndex);
  }, [reorderClipsInTrack, addHistory]);

  // 修剪片段
  const trimClipDuration = useCallback((trackId, clipId, newStartTime, newEndTime) => {
    const track = tracks.find(t => t.id === trackId);
    const clip = track?.clips.find(c => c.id === clipId);
    
    if (!clip) return;

    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.TRIM_CLIP,
      '修剪片段',
      {
        trackId,
        clipId,
        oldStartTime: clip.startTime,
        oldEndTime: clip.endTime,
        newStartTime,
        newEndTime
      }
    ));

    trimClip(trackId, clipId, newStartTime, newEndTime);
  }, [tracks, trimClip, addHistory]);

  // 删除片段
  const removeClip = useCallback((trackId, clipId) => {
    const track = tracks.find(t => t.id === trackId);
    const clip = track?.clips.find(c => c.id === clipId);
    
    if (!clip) return;

    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.DELETE_CLIP,
      '删除片段',
      {
        trackId,
        clipId,
        clipData: clip
      }
    ));

    deleteClip(trackId, clipId);
  }, [tracks, deleteClip, addHistory]);

  // 更新片段信息
  const updateClipInfo = useCallback((trackId, clipId, updates) => {
    const track = tracks.find(t => t.id === trackId);
    const clip = track?.clips.find(c => c.id === clipId);
    
    if (!clip) return;

    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.UPDATE_CLIP,
      '更新片段信息',
      {
        trackId,
        clipId,
        oldData: { title: clip.title, description: clip.description },
        newData: updates
      }
    ));

    updateClip(trackId, clipId, updates);
  }, [tracks, updateClip, addHistory]);

  // 复制片段
  const duplicateClip = useCallback((trackId, clipId) => {
    const track = tracks.find(t => t.id === trackId);
    const clip = track?.clips.find(c => c.id === clipId);
    
    if (!clip) return;

    const newClip = {
      ...clip,
      id: `${clip.id}_copy_${Date.now()}`,
      title: `${clip.title} (副本)`,
      createdAt: new Date().toISOString()
    };

    // 这里需要调用 timelineStore 的 addClip 方法
    // 但由于架构限制，我们通过 updateClip 来实现
    const { addClip } = useTimelineStore.getState();
    addClip(newClip);

    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.ADD_CLIP,
      '复制片段',
      {
        trackId,
        clipId: newClip.id,
        clipData: newClip
      }
    ));
  }, [tracks, addHistory]);

  // 分割片段
  const splitClip = useCallback((trackId, clipId, splitTime) => {
    const track = tracks.find(t => t.id === trackId);
    const clip = track?.clips.find(c => c.id === clipId);
    
    if (!clip || splitTime <= clip.startTime || splitTime >= clip.endTime) return;

    // 创建两个新片段
    const firstClip = {
      ...clip,
      endTime: splitTime,
      duration: splitTime - clip.startTime,
      title: `${clip.title} (1)`
    };

    const secondClip = {
      ...clip,
      id: `${clip.id}_split_${Date.now()}`,
      startTime: splitTime,
      duration: clip.endTime - splitTime,
      title: `${clip.title} (2)`,
      createdAt: new Date().toISOString()
    };

    // 更新第一个片段
    updateClip(trackId, clipId, {
      endTime: firstClip.endTime,
      duration: firstClip.duration,
      title: firstClip.title
    });

    // 添加第二个片段
    const { addClip } = useTimelineStore.getState();
    addClip(secondClip);

    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.UPDATE_CLIP,
      '分割片段',
      {
        trackId,
        clipId,
        splitTime,
        originalClip: clip,
        firstClip,
        secondClip
      }
    ));
  }, [tracks, updateClip, addHistory]);

  // 合并相邻片段
  const mergeClips = useCallback((trackId, clipId1, clipId2) => {
    const track = tracks.find(t => t.id === trackId);
    const clip1 = track?.clips.find(c => c.id === clipId1);
    const clip2 = track?.clips.find(c => c.id === clipId2);
    
    if (!clip1 || !clip2) return;

    // 确定顺序
    const [firstClip, secondClip] = clip1.startTime < clip2.startTime ? [clip1, clip2] : [clip2, clip1];
    
    // 检查是否相邻
    if (Math.abs(firstClip.endTime - secondClip.startTime) > 0.1) {
      console.warn('片段不相邻，无法合并');
      return;
    }

    // 创建合并后的片段
    const mergedClip = {
      ...firstClip,
      endTime: secondClip.endTime,
      duration: secondClip.endTime - firstClip.startTime,
      title: `${firstClip.title} + ${secondClip.title}`,
      description: `${firstClip.description}\n${secondClip.description}`.trim()
    };

    // 更新第一个片段
    updateClip(trackId, firstClip.id, {
      endTime: mergedClip.endTime,
      duration: mergedClip.duration,
      title: mergedClip.title,
      description: mergedClip.description
    });

    // 删除第二个片段
    deleteClip(trackId, secondClip.id);

    // 记录历史
    addHistory(createHistoryAction(
      HISTORY_ACTIONS.UPDATE_CLIP,
      '合并片段',
      {
        trackId,
        mergedClipId: firstClip.id,
        deletedClipId: secondClip.id,
        originalClip1: firstClip,
        originalClip2: secondClip,
        mergedClip
      }
    ));
  }, [tracks, updateClip, deleteClip, addHistory]);

  // 获取片段在轨道中的位置
  const getClipPosition = useCallback((trackId, clipId) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return -1;
    
    return track.clips.findIndex(c => c.id === clipId);
  }, [tracks]);

  // 检查片段是否可以移动到指定位置
  const canMoveClip = useCallback((clipId, fromTrackId, toTrackId, position) => {
    // 基本检查
    if (!clipId || !fromTrackId || !toTrackId) return false;
    
    const fromTrack = tracks.find(t => t.id === fromTrackId);
    const toTrack = tracks.find(t => t.id === toTrackId);
    
    if (!fromTrack || !toTrack) return false;
    
    // 如果是同一轨道，检查位置是否有效
    if (fromTrackId === toTrackId) {
      const currentPosition = getClipPosition(fromTrackId, clipId);
      return position !== currentPosition;
    }
    
    return true;
  }, [tracks, getClipPosition]);

  return {
    dragState,
    startDragClip,
    endDragClip,
    moveClip,
    reorderClips,
    trimClipDuration,
    removeClip,
    updateClipInfo,
    duplicateClip,
    splitClip,
    mergeClips,
    getClipPosition,
    canMoveClip
  };
};